import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Room, RoomEvent, Track, createLocalTracks } from 'livekit-client';
import { Loader2, PhoneOff, Mic, MicOff, Video, VideoOff, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { leaveSession } from '../api/sessions';

/**
 * Phase 38A.4 · Minimal peer-to-peer style LiveKit room for
 * One-to-One sessions.
 *
 * Both participants (creator + user) publish + subscribe. When the
 * viewer clicks "Termina", we disconnect the local Room AND call the
 * backend /leave endpoint so the session state machine transitions
 * correctly (SESSION_STARTED → COMPLETED once both parties have left).
 */

const O2OLiveRoom = ({ joinBundle, sessionId, mode, onExit }) => {
  const { t } = useTranslation('o2o');
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const roomRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });

  const [status, setStatus] = useState('connecting'); // connecting | connected | error | ended
  const [error, setError] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(mode === 'video');
  const [remoteName, setRemoteName] = useState(null);

  useEffect(() => {
    if (!joinBundle?.livekit_url || !joinBundle?.livekit_token) return undefined;
    let cancelled = false;

    const attachRemote = (track, publication, participant) => {
      if (track.kind === Track.Kind.Video && remoteVideoRef.current) {
        track.attach(remoteVideoRef.current);
      }
      if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current);
      }
      if (participant?.name) setRemoteName(participant.name);
    };

    (async () => {
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, attachRemote);
      room.on(RoomEvent.Disconnected, () => {
        if (!cancelled) setStatus('ended');
      });
      room.on(RoomEvent.ParticipantConnected, (p) => {
        if (p?.name) setRemoteName(p.name);
      });

      try {
        await room.connect(joinBundle.livekit_url, joinBundle.livekit_token);
        if (cancelled) return;
        // Create + publish local tracks.
        const tracks = await createLocalTracks({
          audio: true,
          video: mode === 'video',
        });
        for (const t of tracks) {
          await room.localParticipant.publishTrack(t);
          if (t.kind === Track.Kind.Video && localVideoRef.current) {
            t.attach(localVideoRef.current);
            localTracksRef.current.video = t;
          }
          if (t.kind === Track.Kind.Audio) {
            localTracksRef.current.audio = t;
          }
        }
        // Attach any tracks already published by the remote peer.
        room.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => {
            if (pub.track) attachRemote(pub.track, pub, p);
          });
          if (p?.name) setRemoteName(p.name);
        });
        setStatus('connected');
      } catch (e) {
        console.error('[O2O] connect failed:', e);
        if (!cancelled) {
          setError(String(e?.message || e));
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      const room = roomRef.current;
      try {
        Object.values(localTracksRef.current).forEach((t) => {
          if (t) t.stop();
        });
      } catch (_) {
        /* ignore */
      }
      if (room && room.state !== 'disconnected') {
        room.disconnect().catch(() => {});
      }
      roomRef.current = null;
    };
  }, [joinBundle, mode]);

  const toggleMic = async () => {
    const t = localTracksRef.current.audio;
    if (!t) return;
    if (micOn) {
      await t.mute();
    } else {
      await t.unmute();
    }
    setMicOn(!micOn);
  };

  const toggleCam = async () => {
    const t = localTracksRef.current.video;
    if (!t) return;
    if (camOn) {
      await t.mute();
    } else {
      await t.unmute();
    }
    setCamOn(!camOn);
  };

  const handleHangup = async () => {
    try {
      await leaveSession(sessionId);
    } catch (_) {
      /* server-side transition is best-effort */
    }
    const room = roomRef.current;
    if (room && room.state !== 'disconnected') {
      await room.disconnect();
    }
    if (onExit) onExit();
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" data-testid="o2o-live-room">
      <div className="overflow-hidden rounded-3xl border border-border bg-neutral-950 shadow-2xl">
        {status === 'connecting' && (
          <div
            className="flex min-h-[400px] flex-col items-center justify-center gap-3 text-muted-foreground"
            data-testid="o2o-live-room-connecting"
          >
            <Loader2 className="h-8 w-8 animate-spin gold-text" />
            <p className="text-sm">{t('live_room.connecting')}</p>
          </div>
        )}
        {status === 'error' && (
          <div
            className="flex min-h-[400px] flex-col items-center justify-center gap-3 px-6 text-center"
            data-testid="o2o-live-room-error"
          >
            <AlertTriangle className="h-9 w-9 text-red-400" />
            <p className="text-lg font-semibold">{t('live_room.connect_failed_title')}</p>
            <p className="max-w-lg text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={onExit}>{t('live_room.back')}</Button>
          </div>
        )}
        {status !== 'error' && (
          <div className="relative">
            <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
              <div className="relative aspect-video bg-black">
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="h-full w-full object-cover"
                  data-testid="o2o-live-room-remote-video"
                />
                <audio ref={remoteAudioRef} autoPlay />
                <div className="absolute bottom-2 left-2 rounded-full bg-black/50 px-3 py-1 text-xs backdrop-blur">
                  {remoteName || t('live_room.remote_participant')}
                </div>
              </div>
              <div className="relative aspect-video bg-black">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full object-cover"
                  data-testid="o2o-live-room-local-video"
                />
                <div className="absolute bottom-2 left-2 rounded-full bg-black/50 px-3 py-1 text-xs backdrop-blur">
                  {t('live_room.you')}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 bg-neutral-950 p-4">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleMic}
                data-testid="o2o-live-room-mic-btn"
                aria-label={t('live_room.mic_label')}
              >
                {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              </Button>
              {mode === 'video' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleCam}
                  data-testid="o2o-live-room-cam-btn"
                  aria-label={t('live_room.cam_label')}
                >
                  {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                </Button>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={handleHangup}
                data-testid="o2o-live-room-hangup-btn"
                aria-label={t('live_room.hangup_label')}
              >
                <PhoneOff className="mr-2 h-4 w-4" /> {t('live_room.hangup')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default O2OLiveRoom;
